import React, { Suspense, lazy, useEffect } from "react";
import {
  HashRouter as Router,
  Routes,
  Route,
  useLocation,
  Link,
} from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import Home from "./Home";
import About from "./About";
import Settings from "./Settings";
import Icon from "./ui/Icon";
import RouteErrorBoundary from "./RouteErrorBoundary";

const WorkflowEditor = lazy(() => import("./agentHub/components/WorkflowEditor"));
const PaperTrading = lazy(() => import("./paperTrading/PaperTrading"));
const PublicCompetitions = lazy(() => import("./paperTrading/PublicCompetitions"));
const PublicStrategies = lazy(() => import("./paperTrading/PublicStrategies"));
const PublicAccounts = lazy(() => import("./paperTrading/PublicAccounts"));
const PublicAccount = lazy(() => import("./paperTrading/PublicAccount"));
const AdminComments = lazy(() => import("./paperTrading/AdminComments"));
const AppStore = lazy(() => import("./taskApp/AppStore"));
const Register = lazy(() => import("./Register"));
const Login = lazy(() => import("./Login"));
const VoiceTest = lazy(() => import("./voice-test/VoiceTest"));
const AgentHub = lazy(() => import("./agentHub/AgentHub"));
const Founder = lazy(() => import("./Founder"));
const Article = lazy(() => import("./Article"));
const Write = lazy(() => import("./Write"));
const Logout = lazy(() => import("./Logout"));

function AppLayout() {
  const { pathname } = useLocation();
  const isWorkspace =
    pathname.startsWith("/agent-hub") || pathname === "/workflows" || pathname.startsWith("/apps") || pathname.startsWith("/paper-trading") || pathname.startsWith("/competitions") || pathname.startsWith("/strategies") || pathname.startsWith("/accounts");
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className={`kh-app ${isWorkspace ? "kh-app--workspace" : ""}`}>
      <a
        className="ui-skip-link"
        href="#main-content"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById("main-content")?.focus();
        }}
      >
        Skip to content
      </a>
      <Header />
      <main
        id="main-content"
        tabIndex={-1}
        className={`ui-main ${isWorkspace ? "ui-main--workspace" : "ui-main--page"}`}
      >
        <RouteErrorBoundary pathname={pathname}>
          <Suspense
            fallback={
              <div className="ui-loading" role="status">
                <Icon name="spark" size={30} />
                <span>Opening your workspace…</span>
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/about/founder" element={<Founder />} />
              <Route path="/articles/:id" element={<Article />} />
              <Route path="/write" element={<Write />} />
              <Route path="/write/:id" element={<Write />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/workflows" element={<WorkflowEditor />} />
              <Route path="/apps" element={<AppStore />} />
              <Route path="/paper-trading" element={<PaperTrading />} />
              <Route path="/paper-trading/competitions" element={<PaperTrading />} />
              <Route path="/paper-trading/access" element={<PaperTrading />} />
              <Route path="/paper-trading/competitions/:competitionId" element={<PaperTrading />} />
              <Route path="/paper-trading/strategies" element={<PaperTrading />} />
              <Route path="/paper-trading/strategies/:strategyId" element={<PaperTrading />} />
              <Route path="/paper-trading/admin/comments" element={<AdminComments />} />
              <Route path="/strategies/:strategyId" element={<PublicStrategies />} />
              {/* Ahead of the account page for the same reason `/competitions` is: it is the
                  directory the detail pages are reached from, and reading the routes in order should
                  follow the way a visitor arrives. */}
              <Route path="/accounts" element={<PublicAccounts />} />
              <Route path="/accounts/:accountId" element={<PublicAccount />} />
              <Route path="/competitions" element={<PublicCompetitions />} />
              <Route path="/competitions/:competitionId" element={<PublicCompetitions />} />
              <Route path="/competitions/:competitionId/entries/:entryId" element={<PublicCompetitions />} />
              <Route path="/apps/:appId" element={<AppStore />} />
              <Route path="/register" element={<Register />} />
              <Route path="/login" element={<Login />} />
              <Route path="/voice-test" element={<VoiceTest />} />
              <Route path="/agent-hub" element={<AgentHub />} />
              <Route path="/agent-hub/:view" element={<AgentHub />} />
              <Route path="/agent-hub/:view/:agentId" element={<AgentHub />} />
              <Route path="/settings" element={<Settings />} />
              <Route
                path="*"
                element={
                  <div className="ui-page ui-empty-page">
                    <Icon name="search" size={36} />
                    <h1>That page isn’t here.</h1>
                    <p>Let’s get you back to your workspace.</p>
                    <Link className="ui-button ui-button--primary" to="/">
                      Back to overview <Icon name="arrow" />
                    </Link>
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </main>
      {!isWorkspace && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppLayout />
    </Router>
  );
}
